import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Download, Printer, Search,
  FileText, Calendar, RotateCcw
} from 'lucide-react';

function SkeletonRow({ cols = 3 }) {
  return (
    <div className="flex gap-4 items-center py-3 px-6">
      {[...Array(cols)].map((_, i) => (
        <div key={i} className="skeleton h-4 flex-1" />
      ))}
    </div>
  );
}

function EquityRow({ label, amount, bold, highlight, negative }) {
  return (
    <div className={`flex items-center justify-between py-3 px-6 ${bold ? 'font-semibold' : ''} ${highlight ? 'bg-primary-50 dark:bg-primary-900/20 border-y border-primary-200 dark:border-primary-700' : ''}`}>
      <span className={`text-sm ${bold ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>{label}</span>
      <span className={`text-sm ${bold ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'} ${negative ? 'text-danger-600 dark:text-danger-400' : ''}`}>
        {formatCurrency(amount)}
      </span>
    </div>
  );
}

export default function EquityReportPage() {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [period, setPeriod] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/equity', { params: { period } });
      setData(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const formatPeriodLabel = (p) => {
    if (!p) return '';
    const [y, m] = p.split('-');
    const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Laporan Perubahan Modal</h1>
          <p className="page-subtitle">Laporan Perubahan Ekuitas (Equity Statement)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => toast.success('Export PDF - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> PDF
          </button>
          <button onClick={() => toast.success('Export Excel - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </button>
          <button onClick={() => window.print()} className="btn btn-outline btn-sm">
            <Printer className="h-4 w-4 mr-1.5" /> Cetak
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="label">Periode</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="month"
                  className="input pl-9"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-end">
              <button onClick={fetchData} className="btn btn-primary btn-sm">
                <Search className="h-4 w-4 mr-1.5" /> Muat Ulang
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="card">
          <div className="card-header">
            <div className="skeleton h-5 w-48" />
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {[...Array(8)].map((_, i) => <SkeletonRow key={i} />)}
          </div>
        </div>
      ) : !data ? (
        <div className="card">
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Data laporan perubahan modal tidak tersedia untuk periode ini.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              <RotateCcw className="h-5 w-5 mr-2 inline" />
              Laporan Perubahan Modal
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">{formatPeriodLabel(period)}</span>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            <EquityRow label="Modal Awal" amount={data.opening_balance} bold />
            {data.additions?.length > 0 && (
              <div className="py-2">
                <div className="flex items-center justify-between py-1 px-6 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  <span>Penambahan</span>
                  <span>Jumlah</span>
                </div>
                {data.additions.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-6">
                    <span className="text-sm text-gray-700 dark:text-gray-300 pl-4">{item.description}</span>
                    <span className="text-sm text-success-600 dark:text-success-400">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {data.additional_capital > 0 && (
              <EquityRow label="Setoran Modal" amount={data.additional_capital} />
            )}
            {data.drawings > 0 && (
              <EquityRow label="Prive / Penarikan" amount={data.drawings} negative bold />
            )}
            {data.net_income != null && (
              <EquityRow
                label={data.net_income >= 0 ? 'Laba Periode Berjalan' : 'Rugi Periode Berjalan'}
                amount={Math.abs(data.net_income)}
                negative={data.net_income < 0}
                bold
              />
            )}
            {data.corrections > 0 && (
              <EquityRow label="Koreksi" amount={data.corrections} />
            )}
            <div className="border-t-2 border-primary-500">
              <EquityRow label="Modal Akhir" amount={data.closing_balance} bold highlight />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
